import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
} from '@mui/material';
import { 
  Upload, 
  Download, 
  FileUpload, 
  CheckCircle, 
  Error as ErrorIcon
} from '@mui/icons-material';
import Papa from 'papaparse';
import { apiClient } from '../../services/api';

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback for successful imports to refresh tables
  entityType: 'vehicles' | 'locations' | 'jobs';
  projectId: number;
  availableLocations?: Array<{ id: number; name: string; }>;
}

interface ImportResult {
  success: boolean;
  data?: any;
  error?: string;
  rowIndex: number;
}

export const BulkImportDialog: React.FC<BulkImportDialogProps> = ({
  open,
  onClose,
  onSuccess,
  entityType,
  projectId,
  availableLocations = [],
}) => {
  // Unified VRP architecture - get scenario and dataset context
  const { currentScenario, currentDataset } = null /* TODO: Replace with Toolpad navigation */;
  
  // Context validation
  const hasValidContext = projectId && currentScenario && currentDataset;
  
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps = ['Select File', 'Validate Data', 'Import Results'];

  const getTemplateData = () => {
    // Include context IDs in all templates
    const contextFields = {
      project_id: projectId,
      scenario_id: currentScenario?.id || 'REQUIRED',
      dataset_id: currentDataset?.id || 'REQUIRED'
    };
    
    switch (entityType) {
      case 'vehicles':
        return [
          {
            ...contextFields,
            description: 'Delivery Truck 1',
            profile: 'driving',
            // Option 1: Use location references (preferred)
            start_location_id: availableLocations[0]?.id || 1,
            end_location_id: availableLocations[0]?.id || 1,
            // Option 2: Use coordinates (leave empty if using location_id)
            start_lon: '',
            start_lat: '',
            end_lon: '',
            end_lat: '',
            // VROOM capacity array (comma-separated integers)
            capacity: '1000,50',
            skills: '1,2,3',
            // VROOM constraints (all integers)
            max_travel_time: 28800, // 8 hours in seconds
            max_distance: 200000,   // 200km in meters
            max_tasks: 15,
            speed_factor: 1.0,      // Float is OK for speed_factor
            // Time windows (integers - seconds since midnight)
            tw_start: 28800,        // 8:00 AM
            tw_end: 64800,          // 6:00 PM
            // Cost structure (ALL INTEGERS per database schema)
            cost_fixed: 100,        // INTEGER not float
            cost_per_hour: 25,      // INTEGER not float  
            cost_per_km: 2          // INTEGER not float (FIXED: was 1.5)
          },
          {
            ...contextFields,
            description: 'Bike Courier',
            profile: 'cycling',
            start_location_id: availableLocations[1]?.id || 2,
            end_location_id: '',
            start_lon: '',
            start_lat: '',
            end_lon: '',
            end_lat: '',
            capacity: '20',         // Single dimension capacity
            skills: '4,5',
            max_travel_time: 21600, // 6 hours
            max_distance: 50000,    // 50km
            max_tasks: 8,
            speed_factor: 0.8,
            tw_start: 32400,        // 9:00 AM
            tw_end: 61200,          // 5:00 PM
            cost_fixed: 0,          // INTEGER
            cost_per_hour: 15,      // INTEGER
            cost_per_km: 0          // INTEGER
          }
        ];
      case 'jobs':
        return [
          {
            ...contextFields,
            description: 'Delivery to Customer A',
            // Option 1: Use location reference (REQUIRED per Edge Function)
            location_id: availableLocations[0]?.id || 3,
            // Option 2: Use coordinates (leave empty if using location_id)
            location_lon: '',
            location_lat: '',
            // Time specifications (integers - seconds)
            setup: 300,             // 5 minutes setup
            service: 1800,          // 30 minutes service
            // VROOM delivery/pickup arrays (comma-separated integers)
            delivery: '10,5',       // Weight, Volume
            pickup: '0,0',          // Nothing to pickup
            skills: '1,3',          // Required skills
            priority: 3,            // FIXED: Must be 1-5 (was 100)
            // Time windows (HH:MM format)
            time_windows: '09:00-12:00,14:00-17:00' // Multiple windows separated by commas
          },
          {
            ...contextFields,
            description: 'Pickup from Supplier B',
            location_id: availableLocations[1]?.id || 4,
            location_lon: '',
            location_lat: '',
            setup: 600,             // 10 minutes setup
            service: 900,           // 15 minutes service
            delivery: '0,0',
            pickup: '25,15',
            skills: '2',
            priority: 2,            // FIXED: Valid range 1-5 (was 75)
            time_windows: '08:00-10:00' // HH:MM format for clear time specification
          }
        ];
      case 'locations':
        return [
          {
            ...contextFields,
            name: 'Main Depot',
            description: 'Primary distribution center',
            location_lon: -73.9851,
            location_lat: 40.7589,
            address: '123 Warehouse District, New York, NY 10001',
            location_type: 'depot',
            operating_hours: '6:00-22:00',
            contact_info: 'depot@company.com +1-555-0100'
          },
          {
            ...contextFields,
            name: 'Customer Location Alpha',
            description: 'Major retail client',
            location_lon: '',
            location_lat: '',
            address: '456 Broadway, New York, NY 10013',
            location_type: 'customer',
            operating_hours: '9:00-17:00',
            contact_info: 'receiving@alpha.com'
          },
          {
            ...contextFields,
            name: 'Service Point Beta',
            description: 'Emergency service location',
            location_lon: '',
            location_lat: '',
            address: '789 Service Ave, Brooklyn, NY 11201',
            location_type: 'service_point',
            operating_hours: '24/7',
            contact_info: '+1-555-0200'
          }
        ];
      default:
        return [];
    }
  };

  const downloadTemplate = () => {
    const template = getTemplateData();
    
    // Use Papa.unparse with proper CSV configuration
    const csv = Papa.unparse(template as any, {
      quotes: true,        // Force quotes around all fields
      quoteChar: '"',      // Use double quotes
      delimiter: ',',      // Use comma delimiter
      header: true,        // Include headers
      skipEmptyLines: false // Keep structure consistent
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${entityType}_import_template.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validateData = (data: any[]): string[] => {
    const errors: string[] = [];
    
    data.forEach((row, index) => {
      const rowNum = index + 1;
      
      // Common validations
      if (!row.description || row.description.trim() === '') {
        errors.push(`Row ${rowNum}: Description is required`);
      }

      // Entity-specific validations
      switch (entityType) {
        case 'vehicles':
          // Profile validation
          if (!['driving', 'cycling', 'foot', 'car', 'bike'].includes(row.profile)) {
            errors.push(`Row ${rowNum}: Profile must be 'driving', 'cycling', 'foot', 'car', or 'bike'`);
          }
          
          // VROOM Location Validation: Either coordinates OR location_id required
          const hasStartCoords = row.start_lon && row.start_lat;
          const hasStartLocationId = row.start_location_id;
          
          if (!hasStartCoords && !hasStartLocationId) {
            errors.push(`Row ${rowNum}: Either start coordinates (start_lon, start_lat) or start_location_id is required`);
          }
          
          // Validate location IDs if provided
          if (hasStartLocationId && !availableLocations.some(loc => loc.id === parseInt(row.start_location_id))) {
            errors.push(`Row ${rowNum}: Invalid start_location_id (${row.start_location_id})`);
          }
          if (row.end_location_id && !availableLocations.some(loc => loc.id === parseInt(row.end_location_id))) {
            errors.push(`Row ${rowNum}: Invalid end_location_id (${row.end_location_id})`);
          }
          
          // Validate VROOM constraints
          if (row.speed_factor !== undefined && (row.speed_factor <= 0 || row.speed_factor > 5)) {
            errors.push(`Row ${rowNum}: Speed factor must be in range (0, 5]`);
          }
          if (row.max_travel_time !== undefined && row.max_travel_time < 0) {
            errors.push(`Row ${rowNum}: Max travel time must be non-negative`);
          }
          if (row.max_distance !== undefined && row.max_distance < 0) {
            errors.push(`Row ${rowNum}: Max distance must be non-negative`);
          }
          break;
          
        case 'jobs':
          // VROOM Location Validation: Either coordinates OR location_id required
          const hasJobCoords = row.location_lon && row.location_lat;
          const hasJobLocationId = row.location_id;
          
          if (!hasJobCoords && !hasJobLocationId) {
            errors.push(`Row ${rowNum}: Either location coordinates (location_lon, location_lat) or location_id is required`);
          }
          
          if (hasJobLocationId && !availableLocations.some(loc => loc.id === parseInt(row.location_id))) {
            errors.push(`Row ${rowNum}: Invalid location_id (${row.location_id})`);
          }
          
          // Validate priority
          if (row.priority !== undefined && row.priority < 0) {
            errors.push(`Row ${rowNum}: Priority must be non-negative`);
          }
          break;
          
        case 'locations':
          // VROOM Location Validation: Either coordinates OR address required
          const hasCoords = row.location_lon && row.location_lat;
          const hasAddress = row.address && row.address.trim();
          
          if (!hasCoords && !hasAddress) {
            errors.push(`Row ${rowNum}: Either coordinates (location_lon, location_lat) or address is required`);
          }
          
          // Validate location type
          const validTypes = ['depot', 'customer', 'warehouse', 'service_point', 'pickup_point', 'delivery_point'];
          if (!validTypes.includes(row.location_type)) {
            errors.push(`Row ${rowNum}: location_type must be one of: ${validTypes.join(', ')}`);
          }
          
          // Validate operating hours format if provided
          if (row.operating_hours && row.operating_hours !== '24/7' && !/^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(row.operating_hours)) {
            errors.push(`Row ${rowNum}: Operating hours must be in format "HH:MM-HH:MM" or "24/7"`);
          }
          break;
      }
    });
    
    return errors;
  };

  const parseFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value, header) => {
        // Handle empty values
        if (!value || value.trim() === '') {
          return null;
        }
        
        // Handle numeric fields
        const numericFields = [
          'tw_start', 'tw_end', 'setup', 'service', 'priority', 
          'cost_fixed', 'cost_per_hour', 'cost_per_km', 'speed_factor', 
          'max_tasks', 'max_travel_time', 'max_distance',
          'location_lon', 'location_lat', 'start_lon', 'start_lat', 'end_lon', 'end_lat',
          'start_location_id', 'end_location_id', 'location_id'
        ];
        
        if (numericFields.includes(header as any)) {
          const num = parseFloat(value);
          return isNaN(num) ? null : num;
        }
        
        // Handle array fields (capacity, delivery, pickup, skills)
        if (['capacity', 'delivery', 'pickup', 'skills'].includes(header as any)) {
          return value ? value.split(/[,;]/).map((v: string) => parseFloat(v.trim())).filter(v => !isNaN(v)) : [];
        }
        
        // Handle time windows (supports both HH:MM-HH:MM and seconds format)
        if (header === 'time_windows' && value) {
          return value.split(',').map((tw: string) => {
            const parts = tw.trim().split('-');
            if (parts.length === 2) {
              const startPart = parts[0].trim();
              const endPart = parts[1].trim();
              
              // Check if it's in HH:MM format (contains colon)
              if (startPart.includes(':') && endPart.includes(':')) {
                // Convert HH:MM to seconds since midnight
                const parseTimeToSeconds = (timeStr: string): number => {
                  const [hours, minutes] = timeStr.split(':').map(num => parseInt(num));
                  return (hours * 3600) + (minutes * 60);
                };
                
                try {
                  const startSeconds = parseTimeToSeconds(startPart);
                  const endSeconds = parseTimeToSeconds(endPart);
                  return { start: startSeconds, end: endSeconds };
                } catch (e) {
                  console.warn(`Failed to parse time window: ${tw}`);
                  return null;
                }
              } else {
                // Assume it's already in seconds format
                const startSeconds = parseInt(startPart);
                const endSeconds = parseInt(endPart);
                if (!isNaN(startSeconds) && !isNaN(endSeconds)) {
                  return { start: startSeconds, end: endSeconds };
                }
              }
            }
            return null;
          }).filter(tw => tw !== null);
        }
        
        return value.trim();
      },
      complete: (results) => {
        if (results.errors.length > 0) {
          setValidationErrors(['Parse errors: ' + results.errors.map(e => e.message).join(', ')]);
          return;
        }
        
        const data = results.data as any[];
        const errors = validateData(data);
        
        setParsedData(data);
        setValidationErrors(errors);
        setActiveStep(1);
      },
      error: (error) => {
        setValidationErrors(['Failed to parse CSV: ' + error.message]);
      }
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidationErrors([]);
      setParsedData([]);
      parseFile(selectedFile);
    }
  };

  const processImport = async () => {
    if (parsedData.length === 0) return;
    
    setImporting(true);
    setActiveStep(2);
    setProgress(0);
    
    const results: ImportResult[] = [];
    
    for (let i = 0; i < parsedData.length; i++) {
      const row = parsedData[i];
      try {
        // Add context IDs to each row
        const dataWithProject = { 
          ...row, 
          project_id: projectId,
          scenario_id: currentScenario!.id,
          dataset_id: currentDataset!.id
        };
        
        let response;
        switch (entityType) {
          case 'vehicles':
            response = await apiClient.createVehicle(dataWithProject);
            break;
          case 'jobs':
            response = await apiClient.createJob(dataWithProject);
            break;
          case 'locations':
            response = await apiClient.createLocation(dataWithProject);
            break;
        }
        
        results.push({
          success: true,
          data: response,
          rowIndex: i
        });
      } catch (error) {
        results.push({
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          rowIndex: i
        });
      }
      
      setProgress(((i + 1) / parsedData.length) * 100);
    }
    
    setImportResults(results);
    setImporting(false);
    
    // Call onSuccess callback if any imports were successful and refresh tables
    if (results.some(result => result.success) && onSuccess) {
      onSuccess();
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults([]);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Import multiple {entityType} from a CSV file. Download the template to see the required format.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Optimisation-Compliant Import Tips:</strong>
                <br />• Download the template first to see all available optimisation fields
                <br />• <strong>Locations:</strong> Use either coordinates OR location references (flexible!)
                <br />• <strong>Arrays:</strong> Use comma-separated values (e.g., "1000,50" for capacity)
                <br />• <strong>Time format:</strong> Use HH:MM format (e.g., 09:00-17:00) for all time windows
                <br />• <strong>Time windows:</strong> Use "start-end,start-end" format for multiple windows
                <br />• <strong>Skills:</strong> Reference skill IDs with comma separation (e.g., "1,2,3")
                <br />• <strong>Data types:</strong> Cost fields must be integers, priority must be 1-5
                <br />• <strong>Jobs:</strong> location_id is required (coordinates auto-populated)
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadTemplate}
              >
                Download Template
              </Button>
            </Box>

            <TextField
              type="file"
              fullWidth
              inputRef={fileInputRef}
              inputProps={{ accept: '.csv' }}
              onChange={handleFileSelect}
              helperText="Select a CSV file to import"
            />

            {file && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Selected: {file.name} ({Math.round(file.size / 1024)} KB)
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Data Validation
            </Typography>
            
            {validationErrors.length > 0 ? (
              <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Found {validationErrors.length} validation errors:
                  </Typography>
                  {validationErrors.slice(0, 10).map((error, index) => (
                    <Typography key={index} variant="body2">
                      • {error}
                    </Typography>
                  ))}
                  {validationErrors.length > 10 && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      ... and {validationErrors.length - 10} more errors
                    </Typography>
                  )}
                </Alert>
                
                <Typography variant="body2" color="text.secondary">
                  Please fix these errors in your CSV file and try again.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle />
                    <Typography>
                      Validation passed! Found {parsedData.length} valid {entityType} to import.
                    </Typography>
                  </Box>
                </Alert>

                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Description</TableCell>
                        {entityType === 'vehicles' && <TableCell>Profile</TableCell>}
                        {entityType === 'locations' && <TableCell>Type</TableCell>}
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.slice(0, 10).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          {entityType === 'vehicles' && <TableCell>{row.profile}</TableCell>}
                          {entityType === 'locations' && <TableCell>{row.location_type}</TableCell>}
                          <TableCell>
                            <Chip icon={<CheckCircle />} label="Valid" color="success" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {parsedData.length > 10 && (
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Showing first 10 rows of {parsedData.length} total
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Import Results
            </Typography>
            
            {importing && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CircularProgress size={24} />
                  <Typography>Importing {entityType}... ({Math.round(progress)}%)</Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}

            {importResults.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Chip 
                    icon={<CheckCircle />} 
                    label={`${importResults.filter(r => r.success).length} Successful`} 
                    color="success" 
                  />
                  <Chip 
                    icon={<ErrorIcon />} 
                    label={`${importResults.filter(r => !r.success).length} Failed`} 
                    color="error" 
                  />
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResults.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell>{result.rowIndex + 1}</TableCell>
                          <TableCell>{parsedData[result.rowIndex]?.description || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              icon={result.success ? <CheckCircle /> : <ErrorIcon />}
                              label={result.success ? 'Success' : 'Failed'} 
                              color={result.success ? 'success' : 'error'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {result.success ? 'Created successfully' : result.error}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Upload />
          Bulk Import {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!hasValidContext && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="600" gutterBottom>
              Missing Context: Cannot perform bulk import
            </Typography>
            <Typography variant="caption" display="block">
              Please select a scenario and dataset first:
              {!currentScenario && " • No scenario selected"}
              {!currentDataset && " • No dataset selected"}
            </Typography>
            <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
              Use the breadcrumbs above to navigate to Project → Scenario → Dataset first.
            </Typography>
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {hasValidContext && getStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        
        {activeStep === 0 && file && (
          <Button
            variant="contained"
            onClick={() => parseFile(file)}
            disabled={!hasValidContext || validationErrors.length > 0}
          >
            Validate Data
          </Button>
        )}
        
        {activeStep === 1 && validationErrors.length === 0 && (
          <Button
            variant="contained"
            onClick={processImport}
            startIcon={<FileUpload />}
            disabled={!hasValidContext}
          >
            Import {parsedData.length} {entityType}
          </Button>
        )}
        
        {activeStep === 1 && validationErrors.length > 0 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
          >
            Back to File Selection
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};